/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * Header Component
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import { useCountry } from '@context/CountryContext';
import { useToast } from '@context/ToastContext';
import { useSelectableMarkets, useSwitchMarket } from '@hooks/useMarketSwitch';
import { MARKET } from '@utils/market';
import { supportedLanguages, LANGUAGES_ENABLED } from '../../i18n/index';
import './Header.scss';
import logoImg from '@assets/images/vowsera_256x256.png';

const RegionSelector = () => {
  const { i18n } = useTranslation();
  const { countryCode, flag, countries, changeCountry } = useCountry();
  const selectable = useSelectableMarkets();
  const switchMarket = useSwitchMarket();
  const { addToast } = useToast();
  // Only countries in regions that are open (super-admins also see ones in Coming Soon).
  const pickable = countries.filter(c => selectable.includes(c.market));

  // A country in another region switches the region, which reloads the page.
  const pickCountry = async (c) => {
    if (c.market === MARKET) { changeCountry(c.code); setOpen(false); return; }
    try { localStorage.setItem('vowsera-country', c.code); } catch { /* storage blocked */ }
    const result = await switchMarket(c.market);
    if (!result.ok) addToast({ type: 'error', title: 'Region not changed', message: result.message });
  };
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const curLang = (i18n.language || 'en').split('-')[0];
  const curLangInfo = supportedLanguages.find(l => l.code === curLang) || supportedLanguages[0];

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleLangChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
    document.documentElement.setAttribute('lang', code);
  };

  return (
    <div ref={ref} className="region-selector">
      <button onClick={() => setOpen(!open)} className="region-selector__btn">
        {LANGUAGES_ENABLED
          ? `${countryCode} · ${curLangInfo.nativeName} ▾`
          : `${countryCode} ▾`}
      </button>
      {open && (
        <div className="region-selector__dropdown">
          <div className="region-selector__section-label">Country</div>
          {pickable.map(c => (
            <button
              key={c.code}
              onClick={() => pickCountry(c)}
              className={`region-selector__option ${c.code === countryCode ? 'region-selector__option--active' : ''}`}
            >
              {c.flag} {c.name} {c.code === countryCode && '\u2713'}
            </button>
          ))}
          {/* English-only launch \u2014 the language section is gated on
              LANGUAGES_ENABLED. Prior version rendered every locale
              regardless of the master flag; users could flip to
              Hindi / Chinese / etc. and land on a half-translated UI.
              Launch audit 2026-07-08 P0 #3 (public marketing). */}
          {LANGUAGES_ENABLED && (
            <>
              <div className="region-selector__divider" />
              <div className="region-selector__section-label">Language</div>
              {supportedLanguages.map(l => (
                <button
                  key={l.code}
                  onClick={() => handleLangChange(l.code)}
                  className={`region-selector__option ${l.code === curLang ? 'region-selector__option--active' : ''}`}
                >
                  {l.flag} {l.nativeName} {l.code === curLang && '\u2713'}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const links = [
    { to: '/how-it-works', label: 'How it works' },
    { to: '/features', label: 'Features' },
    { to: '/vendors', label: t('nav.vendors') },
    { to: '/venues', label: t('nav.venues') },
    { to: '/planning-tools', label: t('nav.planningTools') },
    { to: '/pricing', label: t('nav.pricing') },
  ];

  const dashLabel = user?.role === 'admin' || user?.role === 'super_admin' ? t('nav.adminPanel') : t('nav.dashboard');
  const dashPath = user?.role === 'vendor' ? '/vendor' :
    user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' :
    user?.role === 'venue_owner' ? '/venue' :
    user?.role === 'coordinator' ? '/coordinator' : '/dashboard';

  return (
    <header className="header">
      <div className="header__container">
        <Link to="/" className="header__logo">
          <img src={logoImg} alt="VowsEra" />
        </Link>

        <nav className="header__nav">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `header__link ${isActive ? 'active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="header__actions">
          <RegionSelector />

          {isAuthenticated ? (
            <>
              <Link to={dashPath} className="btn btn-ghost">{dashLabel}</Link>
              <button onClick={handleLogout} className="btn btn-secondary">{t('nav.logout')}</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost">{t('nav.login')}</Link>
              <Link to="/register" className="btn btn-primary">{t('nav.register')}</Link>
            </>
          )}

          <button className="header__menu-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <i className={`bi bi-${menuOpen ? 'x' : 'list'}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="header__mobile-menu">
          <nav className="header__mobile-nav">
            {links.map(l => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `header__mobile-link ${isActive ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                {l.label}
              </NavLink>
            ))}
            <div className="header__mobile-actions">
              <RegionSelector />
              {isAuthenticated ? (
                <>
                  <Link to={dashPath} className="btn btn-primary w-100" onClick={() => setMenuOpen(false)}>{dashLabel}</Link>
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="btn btn-secondary w-100">{t('nav.logout')}</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-secondary w-100" onClick={() => setMenuOpen(false)}>{t('nav.login')}</Link>
                  <Link to="/register" className="btn btn-primary w-100" onClick={() => setMenuOpen(false)}>{t('nav.register')}</Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
